import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Globe, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Tile = ({ to, onClick, icon: Icon, title, subtitle }) => {
  const body = (
    <>
      <div className="w-10 h-10 rounded-lg bg-[#003366]/10 flex items-center justify-center group-hover:bg-[#003366] transition-colors shrink-0">
        <Icon className="w-5 h-5 text-[#003366] group-hover:text-white transition-colors" />
      </div>
      <div className="flex-1 min-w-0 text-left">
        <p className="font-medium text-sm truncate group-hover:text-[#003366]">{title}</p>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </div>
    </>
  );
  const cls = 'flex items-center gap-3 p-4 bg-white rounded-xl border hover:border-[#003366] hover:shadow-md transition-all group w-full';
  return to ? <Link to={to} className={cls}>{body}</Link> : <button type="button" onClick={onClick} className={cls}>{body}</button>;
};

const Skeleton = () => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
    {Array.from({ length: 12 }).map((_, i) => <div key={i} className="h-20 rounded-xl bg-muted animate-pulse" />)}
  </div>
);

/** Browse by location: countries -> cities -> jobs (the jobs list is the normal search page filtered by country + city). */
export default function JobLocationsPage() {
  const [countries, setCountries] = useState([]);
  const [selected, setSelected] = useState(null);   // { code, name }
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/jobs/locations/countries`)
      .then((res) => setCountries(Array.isArray(res.data) ? res.data : []))
      .catch(() => setError('Could not load countries. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  const openCountry = (c) => {
    setSelected(c);
    setLoading(true);
    setError('');
    axios.get(`${API_URL}/api/jobs/locations/countries/${c.code}/cities`)
      .then((res) => setCities(res.data.cities || []))
      .catch(() => { setCities([]); setError('Could not load cities for this country.'); })
      .finally(() => setLoading(false));
  };

  const back = () => { setSelected(null); setCities([]); setError(''); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8">
        {selected && (
          <button type="button" onClick={back} className="inline-flex items-center gap-1 text-sm text-[#003366] mb-3 hover:underline">
            <ArrowLeft className="w-4 h-4" /> All countries
          </button>
        )}
        <h1 className="text-3xl font-bold text-foreground">
          {selected ? `Jobs in ${selected.name}` : 'Browse Jobs by Location'}
        </h1>
        <p className="text-muted-foreground mt-1">
          {selected ? 'Choose a city to see its jobs' : 'Choose a country, then a city, to see the jobs available there'}
        </p>
      </div>

      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

      {loading ? <Skeleton /> : !selected ? (
        countries.length === 0 ? <p className="text-muted-foreground">No countries available right now.</p> : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {countries.map((c) => (
              <Tile key={c.id} onClick={() => openCountry(c)} icon={Globe} title={c.name}
                subtitle={`${(c.job_count || 0).toLocaleString()} jobs · ${c.city_count || 0} cities`} />
            ))}
          </div>
        )
      ) : (
        <>
          <div className="mb-4">
            <Link to={`/jobs?country=${selected.code}`} className="text-sm text-[#003366] hover:underline">
              View all jobs in {selected.name} →
            </Link>
          </div>
          {cities.length === 0 ? <p className="text-muted-foreground">No cities have been set up for this country yet.</p> : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {cities.map((city) => (
                <Tile key={city.id}
                  to={`/jobs?country=${selected.code}&location=${encodeURIComponent(city.name)}`}
                  icon={MapPin} title={city.name} subtitle={`${(city.job_count || 0).toLocaleString()} jobs`} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
